import { useCallback, useMemo, useState, useRef } from 'react';
import type { FC } from 'react';

import { defineMessages, useIntl } from 'react-intl';

import classNames from 'classnames';

import Overlay from 'react-overlays/Overlay';

import {
  changeComposeVisibility,
  setComposeQuotePolicy,
} from '@/flavours/glitch/actions/compose_typed';
import { openModal } from '@/flavours/glitch/actions/modal';
import type { ApiQuotePolicy } from '@/flavours/glitch/api_types/quotes';
import type { StatusVisibility } from '@/flavours/glitch/api_types/statuses';
import { DropdownSelector } from '@/flavours/glitch/components/dropdown_selector';
import { Icon } from '@/flavours/glitch/components/icon';
import { useAppSelector, useAppDispatch } from '@/flavours/glitch/store';
import AlternateEmailIcon from '@/material-icons/400-24px/alternate_email.svg?react';
import LockIcon from '@/material-icons/400-24px/lock.svg?react';
import PublicIcon from '@/material-icons/400-24px/public.svg?react';
import QuietTimeIcon from '@/material-icons/400-24px/quiet_time.svg?react';

import type { VisibilityModalCallback } from '../../ui/components/visibility_modal';

import { messages as privacyMessages } from './privacy_dropdown';

const messages = defineMessages({
  anyone_quote: {
    id: 'privacy.quote.anyone',
    defaultMessage: '{visibility}, anyone can quote',
  },
  limited_quote: {
    id: 'privacy.quote.limited',
    defaultMessage: '{visibility}, quotes limited',
  },
  disabled_quote: {
    id: 'privacy.quote.disabled',
    defaultMessage: '{visibility}, quotes disabled',
  },
});

interface PrivacyDropdownProps {
  disabled?: boolean;
}

export const VisibilityButton: FC<PrivacyDropdownProps> = (props) => {
  // Standalone: instances without native quote support get a plain visibility
  // dropdown (no quote-policy options).
  const supportsQuotePolicy = useAppSelector(
    (state) => state.compose.get('default_quote_policy') !== undefined,
  );

  if (supportsQuotePolicy) {
    return <PrivacyModalButton {...props} />;
  }

  return <SimplePrivacyDropdown {...props} />;
};

const SimplePrivacyDropdown: FC<PrivacyDropdownProps> = ({
  disabled = false,
}) => {
  const intl = useIntl();
  const dispatch = useAppDispatch();

  const visibility = useAppSelector(
    (state) => state.compose.get('privacy') as StatusVisibility,
  );

  const [open, setOpen] = useState(false);
  const [placement, setPlacement] = useState<'bottom' | 'top'>('bottom');
  const targetRef = useRef<HTMLButtonElement>(null);

  const getTarget = useCallback(() => targetRef.current, []);

  const { icon, iconComponent, text } = useMemo(() => {
    const option = visibilityOptions[visibility];
    return {
      icon: option.icon,
      iconComponent: option.iconComponent,
      text: intl.formatMessage(option.text),
    };
  }, [visibility, intl]);

  const options = useMemo(
    () => [
      {
        icon: 'globe',
        iconComponent: PublicIcon,
        value: 'public',
        text: intl.formatMessage(privacyMessages.public_short),
        meta: intl.formatMessage(privacyMessages.public_long),
      },
      {
        icon: 'unlock',
        iconComponent: QuietTimeIcon,
        value: 'unlisted',
        text: intl.formatMessage(privacyMessages.unlisted_short),
        meta: intl.formatMessage(privacyMessages.unlisted_long),
      },
      {
        icon: 'lock',
        iconComponent: LockIcon,
        value: 'private',
        text: intl.formatMessage(privacyMessages.private_short),
        meta: intl.formatMessage(privacyMessages.private_long),
      },
      {
        icon: 'at',
        iconComponent: AlternateEmailIcon,
        value: 'direct',
        text: intl.formatMessage(privacyMessages.direct_short),
        meta: intl.formatMessage(privacyMessages.direct_long),
      },
    ],
    [intl],
  );

  const handleToggle = useCallback(() => {
    setOpen((prev) => !prev);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  const handleChange = useCallback(
    (value: string) => {
      dispatch(changeComposeVisibility(value as StatusVisibility));
      handleClose();
    },
    [dispatch, handleClose],
  );

  const handleOverlayEnter = useCallback((state: { placement?: string }) => {
    if (state.placement === 'top' || state.placement === 'bottom') {
      setPlacement(state.placement);
    }
  }, []);

  return (
    <>
      <button
        ref={targetRef}
        type='button'
        title={intl.formatMessage(privacyMessages.change_privacy)}
        onClick={handleToggle}
        disabled={disabled}
        className={classNames('dropdown-button', { active: open })}
      >
        <Icon id={icon} icon={iconComponent} />
        <span className='dropdown-button__label'>{text}</span>
      </button>

      <Overlay
        show={open}
        offset={[5, 5]}
        placement={placement}
        flip
        target={getTarget}
        popperConfig={{ strategy: 'fixed', onFirstUpdate: handleOverlayEnter }}
      >
        {({ props }) => (
          <div {...props}>
            <div
              className={`dropdown-animation privacy-dropdown__dropdown simple-privacy-dropdown ${placement}`}
            >
              <DropdownSelector
                items={options}
                value={visibility}
                onClose={handleClose}
                onChange={handleChange}
              />
            </div>
          </div>
        )}
      </Overlay>
    </>
  );
};

const visibilityOptions = {
  public: {
    icon: 'globe',
    iconComponent: PublicIcon,
    value: 'public',
    text: privacyMessages.public_short,
  },
  unlisted: {
    icon: 'unlock',
    iconComponent: QuietTimeIcon,
    value: 'unlisted',
    text: privacyMessages.unlisted_short,
  },
  private: {
    icon: 'lock',
    iconComponent: LockIcon,
    value: 'private',
    text: privacyMessages.private_short,
  },
  direct: {
    icon: 'at',
    iconComponent: AlternateEmailIcon,
    value: 'direct',
    text: privacyMessages.direct_short,
  },
};

const PrivacyModalButton: FC<PrivacyDropdownProps> = ({ disabled = false }) => {
  const intl = useIntl();

  const quotePolicy = useAppSelector(
    (state) => state.compose.get('quote_policy') as ApiQuotePolicy,
  );
  const visibility = useAppSelector(
    (state) => state.compose.get('privacy') as StatusVisibility,
  );

  const { icon, iconComponent } = useMemo(() => {
    const option = visibilityOptions[visibility];
    return { icon: option.icon, iconComponent: option.iconComponent };
  }, [visibility]);
  const text = useMemo(() => {
    const visibilityText = intl.formatMessage(
      visibilityOptions[visibility].text,
    );
    if (visibility === 'private' || visibility === 'direct') {
      return visibilityText;
    }
    if (quotePolicy === 'nobody') {
      return intl.formatMessage(messages.disabled_quote, {
        visibility: visibilityText,
      });
    }
    if (quotePolicy !== 'public') {
      return intl.formatMessage(messages.limited_quote, {
        visibility: visibilityText,
      });
    }
    return intl.formatMessage(messages.anyone_quote, {
      visibility: visibilityText,
    });
  }, [quotePolicy, visibility, intl]);

  const dispatch = useAppDispatch();

  const handleChange: VisibilityModalCallback = useCallback(
    (newVisibility, newQuotePolicy) => {
      if (newVisibility !== visibility) {
        dispatch(changeComposeVisibility(newVisibility));
      }
      if (newQuotePolicy !== quotePolicy) {
        dispatch(setComposeQuotePolicy(newQuotePolicy));
      }
    },
    [dispatch, quotePolicy, visibility],
  );

  const handleOpen = useCallback(() => {
    dispatch(
      openModal({
        modalType: 'COMPOSE_PRIVACY',
        modalProps: { onChange: handleChange },
      }),
    );
  }, [dispatch, handleChange]);

  return (
    <button
      type='button'
      title={intl.formatMessage(privacyMessages.change_privacy)}
      onClick={handleOpen}
      disabled={disabled}
      className={classNames('dropdown-button')}
    >
      <Icon id={icon} icon={iconComponent} />
      <span className='dropdown-button__label'>{text}</span>
    </button>
  );
};
